import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";

import { canUseRemoteStorageUrlDirectly, resolveStoredObjectUrlAsync } from "@/lib/storageObjectUrls";
import { cn } from "@/lib/utils";

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn("relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full", className)}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, src, ...props }, ref) => {
  const [resolvedSrc, setResolvedSrc] = React.useState<string | null>(
    typeof src === "string" && canUseRemoteStorageUrlDirectly("avatars", src) ? src.trim() : null,
  );

  React.useEffect(() => {
    let active = true;

    if (typeof src !== "string" || !src) {
      setResolvedSrc(null);
      return () => {
        active = false;
      };
    }

    if (canUseRemoteStorageUrlDirectly("avatars", src)) {
      setResolvedSrc(src.trim());
      return () => {
        active = false;
      };
    }

    void resolveStoredObjectUrlAsync("avatars", src).then((nextSrc) => {
      if (active) {
        setResolvedSrc(nextSrc);
      }
    });

    return () => {
      active = false;
    };
  }, [src]);

  return <AvatarPrimitive.Image ref={ref} className={cn("h-full w-full object-cover", className)} src={resolvedSrc ?? undefined} {...props} />;
});
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn("flex h-full w-full items-center justify-center rounded-full bg-muted", className)}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarImage, AvatarFallback };
